import { Button, Checkbox, Divider, Input, Popover } from 'antd';
import { PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Loader from 'components/molecules/Loader';

interface ProductSelectDropdownProps {
    disabled: boolean;
    onApply: (selectedIds: number[]) => void;
    existingProductIds?: number[];
    options: { label: string; value: string }[];
    isLoadingOptions: boolean;
}

const ProductSelectDropdown = ({ disabled, onApply, existingProductIds, options, isLoadingOptions }: ProductSelectDropdownProps) => {
    const [open, setOpen] = useState(false);
    const [searchString, setSearchString] = useState('');
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    useEffect(() => {
        if (existingProductIds && existingProductIds.length > 0) {
            setSelectedValues(existingProductIds.map(String));
        }
    }, [existingProductIds]);

    const filteredOptions = useMemo(() => {
        if (!searchString) return options;
        const lower = searchString.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(lower));
    }, [options, searchString]);

    const handleCheckboxChange = useCallback((value: string, checked: boolean) => {
        setSelectedValues((prev) => (checked ? [...prev, value] : prev.filter((v) => v !== value)));
    }, []);

    const handleSelectAll = useCallback(
        (checked: boolean) => {
            if (checked) {
                const allValues = filteredOptions.map((o) => o.value);
                setSelectedValues((prev) => {
                    const set = new Set([...prev, ...allValues]);
                    return Array.from(set);
                });
            } else {
                const optionValues = new Set(filteredOptions.map((o) => o.value));
                setSelectedValues((prev) => prev.filter((v) => !optionValues.has(v)));
            }
        },
        [filteredOptions],
    );

    const handleApply = useCallback(() => {
        const numericIds = selectedValues.map((v) => Number(v)).filter((n) => !isNaN(n));
        onApply(numericIds);
        setOpen(false);
    }, [selectedValues, onApply]);

    const handleReset = useCallback(() => {
        setSelectedValues([]);
        setSearchString('');
    }, []);

    const handleOpenChange = useCallback((newOpen: boolean) => {
        setOpen(newOpen);
        if (!newOpen) {
            setSearchString('');
        }
    }, []);

    const allVisibleSelected = useMemo(() => {
        if (filteredOptions.length === 0) return false;
        return filteredOptions.every((o) => selectedValues.includes(o.value));
    }, [filteredOptions, selectedValues]);

    const someVisibleSelected = useMemo(() => {
        if (filteredOptions.length === 0) return false;
        const count = filteredOptions.filter((o) => selectedValues.includes(o.value)).length;
        return count > 0 && count < filteredOptions.length;
    }, [filteredOptions, selectedValues]);

    const dropdownContent = (
        <div className="w-fit flex flex-col">
            <div>
                <Input
                    placeholder="Search Product..."
                    prefix={<SearchOutlined className="text-[#878787]" />}
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    allowClear
                    size="small"
                    bordered={false}
                    style={{ backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: '4px', height: '32px' }}
                />
            </div>

            <div className="h-7 flex items-center px-2">
                <span className="text-xs text-[#807e86]">Select Product(s)</span>
            </div>

            {isLoadingOptions ? (
                <div className="flex justify-center items-center py-6">
                    <Loader />
                </div>
            ) : filteredOptions.length === 0 ? (
                <div className="flex justify-center items-center py-6 text-gray-400 text-sm">No products found</div>
            ) : (
                <>
                    <div className="h-7 flex items-center px-2 rounded">
                        <Checkbox
                            checked={allVisibleSelected}
                            indeterminate={someVisibleSelected}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                        >
                            <span className="text-sm text-[#18181B]">Select All</span>
                        </Checkbox>
                    </div>

                    <div className="max-h-[272px] overflow-y-auto">
                        {filteredOptions.map((option) => (
                            <div key={option.value} className="h-7 flex items-center px-2 rounded">
                                <Checkbox
                                    checked={selectedValues.includes(option.value)}
                                    onChange={(e) => handleCheckboxChange(option.value, e.target.checked)}
                                >
                                    <span className="text-sm text-[#18181B] truncate">{option.label}</span>
                                </Checkbox>
                            </div>
                        ))}
                        <Divider className="my-1" />
                    </div>
                </>
            )}

            <div className="flex justify-center items-center pt-3 pb-1 px-1 border-t border-[#0000000f]">
                <div className="flex gap-x-2">
                    <Button onClick={handleReset}>Reset</Button>
                    <Button type="primary" disabled={selectedValues.length === 0} onClick={handleApply}>
                        Apply
                    </Button>
                </div>
            </div>
        </div>
    );

    return (
        <Popover
            content={disabled ? null : dropdownContent}
            trigger="click"
            open={!disabled && open}
            onOpenChange={handleOpenChange}
            placement="bottomRight"
            overlayInnerStyle={{ padding: 0, borderRadius: '8px' }}
        >
            <Button disabled={disabled}>
                <div className="flex flex-row items-center gap-x-1">
                    <PlusCircleOutlined />
                    <p className="text-[#18181B] text-sm font-medium">Add Products</p>
                </div>
            </Button>
        </Popover>
    );
};

ProductSelectDropdown.displayName = 'ProductSelectDropdown';

export default ProductSelectDropdown;
